import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { QUERY_KEYS } from './api';

type Id = string | number | null | undefined;
type Filters = Record<string, unknown>;

const id = (value: Id) => String(value);

export const invalidateGroups = (queryClient: QueryClient, roots: readonly QueryKey[]) =>
  roots.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));

export const serviceVendorKeys = {
  all: [QUERY_KEYS.SERVICE_VENDORS] as const,
  list: (filters: Filters) => [...serviceVendorKeys.all, 'list', filters] as const,
  detail: (serviceVendorId: Id) => [...serviceVendorKeys.all, 'detail', id(serviceVendorId)] as const,
};

export const serviceTypeKeys = {
  all: [QUERY_KEYS.SERVICE_TYPES] as const,
};

export const featureFlagKeys = {
  all: [QUERY_KEYS.FEATURE_FLAGS] as const,
};

export const notificationBroadcastKeys = {
  all: [QUERY_KEYS.NOTIFICATION_BROADCASTS] as const,
};

export const vendorServiceKeys = {
  all: [QUERY_KEYS.VENDOR_SERVICES] as const,
  forVendor: (serviceVendorId: Id) => [...vendorServiceKeys.all, id(serviceVendorId)] as const,
};

export const businessHoursKeys = {
  all: [QUERY_KEYS.VENDOR_BUSINESS_HOURS] as const,
  forVendor: (serviceVendorId: Id) => [...businessHoursKeys.all, id(serviceVendorId)] as const,
};

export const employeeKeys = {
  all: [QUERY_KEYS.EMPLOYEES] as const,
  forVendor: (serviceVendorId: Id) => [...employeeKeys.all, id(serviceVendorId)] as const,
};

export const employeeScheduleKeys = {
  all: [QUERY_KEYS.EMPLOYEE_SCHEDULES] as const,
  forEmployee: (serviceVendorId: Id, employeeId: Id) => [...employeeScheduleKeys.all, id(serviceVendorId), id(employeeId)] as const,
  forMonth: (serviceVendorId: Id, employeeId: Id, month?: number, year?: number) =>
    [...employeeScheduleKeys.forEmployee(serviceVendorId, employeeId), month, year] as const,
};

export const employeeTimeOffKeys = {
  all: [QUERY_KEYS.EMPLOYEE_TIME_OFFS] as const,
  forEmployee: (serviceVendorId: Id, employeeId: Id) => [...employeeTimeOffKeys.all, id(serviceVendorId), id(employeeId)] as const,
  forMonth: (serviceVendorId: Id, employeeId: Id, month?: number, year?: number) =>
    [...employeeTimeOffKeys.forEmployee(serviceVendorId, employeeId), month, year] as const,
};

export const employeeCalendarKeys = {
  all: [QUERY_KEYS.EMPLOYEE_CALENDAR] as const,
  forEmployee: (serviceVendorId: Id, employeeId: Id) => [...employeeCalendarKeys.all, id(serviceVendorId), id(employeeId)] as const,
};

export const reservationKeys = {
  all: [QUERY_KEYS.RESERVATIONS] as const,
  list: (filters: Filters) => [...reservationKeys.all, 'list', filters] as const,
  detail: (serviceVendorId: Id, reservationId: Id) => [...reservationKeys.all, 'detail', id(serviceVendorId), id(reservationId)] as const,
  calendar: (serviceVendorId: Id, employeeId: Id, month: number, year: number) =>
    [...reservationKeys.all, 'calendar', id(serviceVendorId), id(employeeId), month, year] as const,
};

export const externalReservationKeys = {
  all: [QUERY_KEYS.EXTERNAL_RESERVATIONS] as const,
  list: (filters: Filters) => [...externalReservationKeys.all, 'list', filters] as const,
  detail: (serviceVendorId: Id, employeeId: Id, externalReservationId: Id) =>
    [...externalReservationKeys.all, 'detail', id(serviceVendorId), id(employeeId), id(externalReservationId)] as const,
};

export const dashboardKeys = {
  weeklySummaryRoot: [QUERY_KEYS.WEEKLY_SUMMARY] as const,
  weeklyCalendarRoot: [QUERY_KEYS.WEEKLY_CALENDAR] as const,
  weeklySummary: (serviceVendorId: Id, weekStart: string) => [...dashboardKeys.weeklySummaryRoot, id(serviceVendorId), weekStart] as const,
  weeklyCalendar: (serviceVendorId: Id, weekStart: string) => [...dashboardKeys.weeklyCalendarRoot, id(serviceVendorId), weekStart] as const,
};

export const subscriptionKeys = {
  all: [QUERY_KEYS.SUBSCRIPTION] as const,
  forVendor: (serviceVendorId: Id) => [...subscriptionKeys.all, id(serviceVendorId)] as const,
};

export const pricingConfigKeys = {
  all: [QUERY_KEYS.PRICING_CONFIG] as const,
  forVendor: (serviceVendorId: Id) => [...pricingConfigKeys.all, id(serviceVendorId)] as const,
};

export const internalNoteKeys = {
  all: [QUERY_KEYS.INTERNAL_NOTES] as const,
  forPet: (serviceVendorId: Id, petId: Id) => [...internalNoteKeys.all, id(serviceVendorId), id(petId)] as const,
};

export const medicalDocumentKeys = {
  all: [QUERY_KEYS.MEDICAL_DOCUMENTS] as const,
  forReservation: (reservationId: Id) => [...medicalDocumentKeys.all, id(reservationId)] as const,
};

export const petHealthProfileKeys = {
  all: [QUERY_KEYS.PET_HEALTH_PROFILE] as const,
  forReservation: (reservationId: Id) => [...petHealthProfileKeys.all, id(reservationId)] as const,
};

export const claimRequestKeys = {
  all: [QUERY_KEYS.CLAIM_REQUESTS] as const,
  list: (filters: Filters) => [...claimRequestKeys.all, 'list', filters] as const,
  detail: (claimRequestId: Id) => [...claimRequestKeys.all, 'detail', id(claimRequestId)] as const,
};

export const appointmentRequestKeys = {
  all: [QUERY_KEYS.APPOINTMENT_REQUESTS] as const,
  list: (filters: Filters) => [...appointmentRequestKeys.all, 'list', filters] as const,
};

export const invalidateReservationViews = (queryClient: QueryClient) =>
  invalidateGroups(queryClient, [
    reservationKeys.all,
    externalReservationKeys.all,
    dashboardKeys.weeklySummaryRoot,
    dashboardKeys.weeklyCalendarRoot,
  ]);

export const invalidateClaimReviewViews = (queryClient: QueryClient) => invalidateGroups(queryClient, [claimRequestKeys.all, serviceVendorKeys.all]);
